import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { type FunctionComponent, useState, useEffect } from 'react';
import {
	type Maybe,
	type ProductOptionFieldsFragment,
	type ProductOptionGroupFieldsFragment
} from '../../../__generated__/graphql-client-types';
import { REQUIRED_MESSAGE } from '../../../constants/product';
import { AnalyticsHelper } from '../../../helpers/analytics/analytics.helper';
import { changeOption } from '../../../helpers/product-helper/configuration/product-change-option.helper';
import { findGroupSelectionById, updateGroupOption } from '../../../helpers/product-helper/configuration/product-configuration.helper';
import { updateOptionResource } from '../../../helpers/product-helper/configuration/product-option.helper';
import { buildGTMRemoveConfigurationNestedOption } from '../../../helpers/product-helper/product-analytics.helper';
import { type OptionSelection, type ProductOptionSelection, type Selection } from '../../../types/product-option.types';
import { type ProductResourceVariantCard } from '../../../types/product.types';
import { useTrackConfigurationOptionChange } from '../product-option/product-option.component';
import { ProductCardActions } from '../product-option-card/product-card-actions/product-card-actions.component';
import { ProductOptionCardCarousel } from '../product-option-card/product-option-cards.component';
import { ProductSelectionCard } from '../product-option-card/product-selection-card.component';

type ConfigurableOptions = {
	variantId: number;
	quantity: number;
};

export type ProductOptionInlineNestedProps = {
	optionSelections: OptionSelection[];
	selection?: Selection;
	changeSelection?: (selection?: Selection) => void;
	selected?: Maybe<ProductOptionFieldsFragment>;
} & Pick<ProductOptionSelection, 'update'> &
	ProductOptionGroupFieldsFragment;

export const ProductOptionInlineNested: FunctionComponent<ProductOptionInlineNestedProps> = ({
	optionSelections,
	selection,
	selected,
	update,
	...group
}) => {
	const groupId = kebabCase(group.label);
	const initSelectedOption = selected || null;
	const initResource = selected && selected.resource ? selected.resource : null;
	const initVariant = initResource ? initResource.finishes[0] : null;
	const [selectedProduct, setSelectedProduct] = useState<Maybe<ProductOptionFieldsFragment>>(initSelectedOption);
	const selectedOption = selectedProduct ? optionSelections.find(({ value }) => value === selectedProduct.value) : null;
	const [selectedVariant, setSelectedVariant] = useState<Maybe<ProductResourceVariantCard>>(initVariant);
	const [selectedQuantity, setSelectedQuantity] = useState<number | undefined>(initResource?.quantity || 1);
	const trackChangeOption = useTrackConfigurationOptionChange();

	/**
	 * Confirm Selection Handler
	 */
	const confirmSelection = (option: ProductOptionFieldsFragment, selectedOptions: ConfigurableOptions) => {
		const { resource } = option;
		const optionSelection = optionSelections.find((currentOption) => currentOption.id === kebabCase(option.name));
		const selectedNestedGroup = findGroupSelectionById(groupId, selection);
		const selectedFinish = resource?.finishes.find((finish) => finish.id === selectedOptions.variantId);
		if (resource && optionSelection && update && selection && selectedNestedGroup && selectedFinish) {
			const selectedVariantId = selectedOptions.variantId.toString();
			const updatedResource = updateOptionResource(resource, selectedFinish, selectedQuantity);
			const updatedParams = { ...optionSelection.params, nested: { selectedVariantId, selectedQuantity } };
			const updatedOption = changeOption({ ...option, resource: updatedResource }, optionSelections);
			update(
				updatedOption,
				{ ...optionSelection, price: selectedFinish.price, params: updatedParams },
				{ ...selectedNestedGroup, valid: true }
			);
			setSelectedProduct(updatedOption);
			void trackChangeOption(group, option, selectedOptions);
		}
	};
	/**
	 * Clear Selection Handler
	 */
	const clearSelection = () => {
		const selectedNestedGroup = findGroupSelectionById(groupId, selection);
		if (selectedProduct) {
			const selectedName = selectedProduct.name;
			const optionSelection = optionSelections.find((currentOption) => currentOption.id === kebabCase(selectedName));
			updateGroupOption(update, selectedNestedGroup, selectedProduct, selection, optionSelection);
			void AnalyticsHelper.track(buildGTMRemoveConfigurationNestedOption());
		}
		setSelectedProduct(null);
	};

	const variantChange = (variantSelected: ProductResourceVariantCard) => setSelectedVariant(variantSelected);
	const quantityChange = (quantityChanged?: number) => setSelectedQuantity(quantityChanged);

	const selectProduct = (option: ProductOptionFieldsFragment) => {
		if (option.resource) {
			const selectedFinish =
				option.resource?.finishes.find((finish) => finish.id === option.resource?.variantId) || option.resource?.finishes[0];
			variantChange(selectedFinish);
			confirmSelection(option, { variantId: selectedFinish?.id, quantity: 1 });
		}
	};
	useEffect(() => {
		// If the selected option stored is not selected (due to a reset from outside this component's scope),
		// clear the selection.
		if (!selectedOption?.selected) {
			clearSelection();
		}
	}, [selectedOption?.selected]);

	useEffect(() => {
		if (selectedProduct && selectedVariant && selectedQuantity !== undefined) {
			confirmSelection(selectedProduct, { variantId: selectedVariant.id, quantity: selectedQuantity });
		}
	}, [selectedVariant, selectedQuantity]);

	return (
		<>
			<span className="pb1 f5 lh-copy">{REQUIRED_MESSAGE}</span>
			{selectedProduct && selectedVariant ? (
				<ProductSelectionCard
					{...selectedProduct}
					resource={selectedProduct.resource}
					variant={selectedVariant}
					quantity={selectedQuantity}
					variantChange={variantChange}
					quantityChange={quantityChange}>
					<ProductCardActions
						className="items-center-ns justify-end-ns mt3"
						link={{ name: 'View Full Product Details', url: selectedProduct.resource?.url || '', openInNewTab: true }}
						action={{ label: 'Remove Item', onClick: clearSelection, buttonStyle: 'DANGER' }}
					/>
				</ProductSelectionCard>
			) : (
				<ProductOptionCardCarousel name={group.label} options={group.options} onSelect={selectProduct} />
			)}
		</>
	);
};
